import { useCallback } from 'react';

import { useNavigate } from 'react-router-dom';

import { ROUTES } from '@constants/routes';
import { USER_TYPE } from '@constants/user.constants';
import { getErrorMessage } from '@services/api/errorHandler';
import { loginRequest } from '@services/auth';
import { useAuthStore } from '@store';

const INVALID_CREDENTIALS_MESSAGE = 'Invalid email or password.';

const useAuth = () => {
  const navigate = useNavigate();
  const user = useAuthStore((state) => state.user);
  const accessToken = useAuthStore((state) => state.accessToken);
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
  const isHydrated = useAuthStore((state) => state.isHydrated);
  const setAuth = useAuthStore((state) => state.setAuth);
  const clearAuth = useAuthStore((state) => state.clearAuth);
  const hydrateAuth = useAuthStore((state) => state.hydrateAuth);

  const login = useCallback(
    async ({ email, password }) => {
      const response = await loginRequest(
        { email, password },
        {
          silent: true,
        },
      );

      const payload = response?.data;
      const token = payload?.data?.token;
      const userData = payload?.data?.userData;

      if (!payload?.isSuccess || !token || !userData) {
        throw new Error(payload?.message || 'Login failed.');
      }

      if (!userData.isVerified) {
        throw new Error('Please verify your email before signing in.');
      }

      if (userData.user_type !== USER_TYPE.ADMIN) {
        throw new Error(INVALID_CREDENTIALS_MESSAGE);
      }

      setAuth({
        accessToken: token,
        user: userData,
      });

      return payload;
    },
    [setAuth],
  );

  const logout = useCallback(() => {
    clearAuth();
    navigate(ROUTES.LOGIN, { replace: true });
  }, [clearAuth, navigate]);

  return {
    user,
    accessToken,
    isAuthenticated,
    isHydrated,
    setAuth,
    clearAuth,
    hydrateAuth,
    login,
    logout,
    getErrorMessage,
  };
};

export default useAuth;
